import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { assetsApi, type AssetDetail } from "../api/assets";
import type { AttributeValue } from "../api/catalog";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { codebookWrite } from "../lib/codebookFunctions";
import { useToast } from "../components/Toast";
import { usePrompt } from "../components/ConfirmDialog";
import { AttributeValuesForm, compactAttributeValues, missingRequiredAttributes } from "../components/AttributeValuesForm";
import { AttributeValuesView } from "../components/AttributeValuesView";
import { AssetOriginFields } from "../components/AssetOriginFields";
import { MapPicker } from "../components/MapPicker";
import { ConditionChip, UsageChip } from "../components/ConditionChip";
import { ReplacementBadge } from "../components/AssetSummaryCard";
import { AssetEventsTable } from "../components/AssetEventsTable";
import { AssetFormModal, translateAssetError } from "../components/AssetFormModal";
import { MountModal } from "../components/MountModal";
import { DismountModal } from "../components/DismountModal";
import { ConditionModal, MoveAssetModal, NoteEventModal } from "../components/AssetActionModals";
import { QrCode } from "../components/QrCode";
import { AttachmentsPanel } from "../components/AttachmentsPanel";
import { CommentsPanel } from "../components/CommentsPanel";
import { PermLink } from "../components/PermLink";
import { PlacementPreview } from "../components/layout/PlacementPreview";
import { layoutsApi, type PlacementView } from "../api/layouts";
import { formatDate, formatDateTime } from "../lib/dates";
import Ic from "../components/icons.jsx";

type ModalState = "mount" | "remount" | "dismount" | "move" | "condition" | "note" | "edit" | null;

/** `/asseti/:id` — asset karton (contract §7). */
export default function AssetPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const prompt = usePrompt();
  const { hasPermission } = useAuth();
  const canMount = hasPermission("assets:mount");
  // Sprint 7: assets:edit (edit, note), assets:condition, assets:delete.
  const canEdit = hasPermission("assets:edit");
  const canDelete = hasPermission("assets:delete");
  const canCondition = hasPermission("assets:condition");
  // Neighbouring modules on the card (audit 2026-09-23 §C): a link or a section only with their view.
  const canViewTypes = hasPermission("types:view");
  // The supplier's card opens with any of the codebook's functions (same rule as its route).
  const canOpenSupplier = codebookWrite("suppliers").some((p) => hasPermission(p));
  const canViewLayouts = hasPermission("layouts:view");

  const [asset, setAsset] = useState<AssetDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [eventsKey, setEventsKey] = useState(0);

  // Inline PUT form: actual values + piece data.
  const [values, setValues] = useState<Record<string, AttributeValue>>({});
  const [serialNo, setSerialNo] = useState("");
  const [manufacturerId, setManufacturerId] = useState("");
  const [manufacturerCode, setManufacturerCode] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [isCustomMade, setIsCustomMade] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Values and piece data are read-only until "Izmeni" (nothing changes by accident).
  const [editingPiece, setEditingPiece] = useState(false);

  const apply = useCallback((d: AssetDetail) => {
    setAsset(d);
    setValues({ ...d.actualValues });
    setSerialNo(d.serialNo ?? "");
    setManufacturerId(d.manufacturerId ?? "");
    setManufacturerCode(d.manufacturerCode ?? "");
    setSupplierId(d.supplierId ?? "");
    setIsCustomMade(d.isCustomMade);
    setNote(d.note ?? "");
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setAsset(null);
    setError(null);
    assetsApi.get(id)
      .then((d) => { if (!cancelled) apply(d); })
      .catch((err: ApiError) => { if (!cancelled) setError(err.status === 404 ? t("assets.notFound", "Asset ne postoji.") : err.message); });
    return () => { cancelled = true; };
  }, [id, apply, t]);

  const loadEvents = useCallback((page: number, size: number) => assetsApi.events(id ?? "", { page, size }), [id]);

  // "Gde je na layoutu" (sprint 6 §5): the mounted position's placements; [] when none / not mounted.
  const [placements, setPlacements] = useState<PlacementView[]>([]);
  const mountedPositionId = asset?.usage === "MONTIRAN" ? asset.positionId : null;
  useEffect(() => {
    if (!canViewLayouts || !mountedPositionId) { setPlacements([]); return; }
    let cancelled = false;
    layoutsApi.placements(mountedPositionId)
      .then((list) => { if (!cancelled) setPlacements(list); })
      .catch(() => { if (!cancelled) setPlacements([]); });
    return () => { cancelled = true; };
  }, [canViewLayouts, mountedPositionId]);

  /** After any event-producing action: refresh the card and the history. */
  function afterEvent(d: AssetDetail, message: string) {
    setModal(null);
    apply(d);
    setEventsKey((k) => k + 1);
    toast.success(message);
  }

  async function savePiece() {
    if (!asset) return;
    const missing = missingRequiredAttributes(asset.attributeDefs, values);
    if (missing.length > 0) {
      toast.error(t("assets.requiredMissing", "Obavezno: {{list}}", { list: missing.map((d) => d.label).join(", ") }));
      return;
    }
    setSaving(true);
    try {
      const saved = await assetsApi.update(asset.id, {
        tag: asset.tag,
        serialNo: serialNo.trim() || null,
        typeId: asset.typeId,
        manufacturerId: manufacturerId || null,
        manufacturerCode: manufacturerCode.trim() || null,
        supplierId: supplierId || null,
        isCustomMade,
        actualValues: compactAttributeValues(values),
        note: note.trim() || null,
      });
      apply(saved);
      setEditingPiece(false);
      toast.success(t("assets.toast.saved", "Asset sačuvan."));
    } catch (err) {
      const ae = err as ApiError;
      const key = (ae.details as unknown as Record<string, string> | undefined)?.key;
      const label = key ? (asset.attributeDefs.find((d) => d.key === key)?.label ?? key) : "";
      if (ae.code === "UNKNOWN_ATTRIBUTE") toast.error(t("assets.error.unknownAttribute", "Nepoznat atribut: {{key}}", { key: label }));
      else if (ae.code === "INVALID_ATTRIBUTE_VALUE") toast.error(t("assets.error.invalidAttributeValue", "Neispravna vrednost atributa: {{key}}", { key: label }));
      else toast.error(translateAssetError(t, ae));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!asset) return;
    const reason = await prompt({
      title: t("assets.confirmDelete.title", "Brisanje asseta"),
      message: t("assets.confirmDelete.message", "Obrisati asset „{{tag}}“? Istorija ostaje u audit logu.", { tag: asset.tag }),
      confirmLabel: t("action.delete", "Obriši"),
      placeholder: t("deleteReason.placeholder", "Razlog brisanja (obavezno) — npr. rashodovano, zamenjeno, greška pri unosu…"),
      multiline: true,
      required: true,
      danger: true,
    });
    if (!reason) return;
    try {
      await assetsApi.remove(asset.id, reason);
      toast.success(t("assets.toast.deleted", "Asset obrisan."));
      navigate("/asseti", { replace: true });
    } catch (err) {
      const ae = err as ApiError;
      toast.error(ae.code === "MOUNTED" ? t("assets.error.deleteMounted", "Montiran asset se ne briše — prvo ga demontirajte.") : ae.message);
    }
  }

  if (error) {
    return (
      <div>
        <div className="page-head"><div><h1>{t("assets.karton", "Karton asseta")}</h1></div></div>
        <div className="card"><div style={errorBox} role="alert">{error}</div></div>
      </div>
    );
  }
  if (!asset) {
    return <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>;
  }

  const mounted = asset.usage === "MONTIRAN";
  const positionCrumbs = asset.positionPath;

  return (
    <div className="asset-page">
      <nav className="crumbs" aria-label={t("structure.field.path", "Putanja")}>
        <span><Link to="/asseti">{t("assets.title", "Asseti")}</Link></span>
        <span>{asset.tag}</span>
      </nav>

      {/* Header: tag + QR + chips + actions */}
      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-b asset-head">
          <QrCode value={asset.tag} size={112} className="asset-head-qr" alt={t("assets.qrAlt", "QR kod {{tag}}", { tag: asset.tag })} />
          <div className="asset-head-id">
            <h1 className="asset-tag-h1">{asset.tag}</h1>
            <span className="tag mono" title={t("assets.field.inventoryNo", "Sistemski ID")}>{asset.inventoryNo}</span>
            <div className="asset-chips">
              <ConditionChip condition={asset.condition} />
              <UsageChip usage={asset.usage} />
              <ReplacementBadge asset={asset} />
            </div>
          </div>
          <div className="asset-head-type">
            <div className="muted" style={{ fontSize: 13 }}>
              <span className="mono">{asset.typeCode}</span> {asset.typeName}
              {asset.manufacturer && <span> · {asset.manufacturer}{asset.manufacturerCode ? ` ${asset.manufacturerCode}` : ""}</span>}
              {asset.serialNo && <span> · {t("assets.field.serialNoShort", "S/N")} <span className="mono">{asset.serialNo}</span></span>}
              {asset.isCustomMade && <span className="tag info" style={{ marginLeft: 8 }}>{t("assets.customMadeTag", "sopstvena izrada")}</span>}
            </div>
            {asset.expectedTypeMismatch && (
              <div className="note-intervention" role="alert" style={{ marginTop: 10 }}>
                <div className="src"><Ic.alert /> {t("assets.mismatch.title", "Tip ne odgovara poziciji")}</div>
                {t("assets.mismatch.body", "Asset je montiran na poziciju čiji očekivani tip ne odgovara njegovom tipu.")}
              </div>
            )}
          </div>
          <div className="page-actions asset-head-actions">
            <Link to={`/asseti/${asset.id}/nalepnica`} className="btn" style={{ textDecoration: "none" }}>
              <Ic.print /> {t("assets.printLabel", "Štampaj nalepnicu")}
            </Link>
          </div>
        </div>
        {(canMount || canEdit || canDelete || canCondition) && (
          <div className="card-b asset-actions" style={{ paddingTop: 0 }}>
            {canMount && (
              <>
                {mounted ? (
                  <>
                    <button type="button" className="btn primary lg" onClick={() => setModal("dismount")}>{t("assets.action.dismount", "Demontiraj")}</button>
                    <button type="button" className="btn lg" data-tip={t("assets.action.remountHint", "Na drugu poziciju, bez skidanja na lager")} onClick={() => setModal("remount")}>
                      <Ic.arrowRight /> {t("assets.action.remount", "Premesti")}
                    </button>
                  </>
                ) : (
                  <>
                    <span className="tip-wrap" data-tip={asset.condition !== "ISPRAVAN" ? t("mount.error.notOperational", "Asset nije ispravan — montira se samo ispravan asset.") : undefined}>
                      <button type="button" className="btn primary lg" disabled={asset.condition !== "ISPRAVAN"} onClick={() => setModal("mount")}>
                        {t("assets.action.mount", "Montiraj")}
                      </button>
                    </span>
                    <button type="button" className="btn lg" onClick={() => setModal("move")}>{t("assets.action.move", "Premesti")}</button>
                  </>
                )}
              </>
            )}
            {canCondition && (
              <button type="button" className="btn lg" onClick={() => setModal("condition")}>{t("assets.action.condition", "Promeni stanje")}</button>
            )}
            {canEdit && (
              <>
                <button type="button" className="btn lg" onClick={() => setModal("note")}>{t("assets.action.note", "Dodaj zapis")}</button>
                <button type="button" className="btn lg" onClick={() => setModal("edit")}>{t("action.edit", "Izmeni")}</button>
              </>
            )}
            {canDelete && (
              <span className="tip-wrap" data-tip={mounted ? t("assets.error.deleteMounted", "Montiran asset se ne briše — prvo ga demontirajte.") : undefined}>
                <button type="button" className="btn lg" style={dangerBtn} disabled={mounted} onClick={handleDelete}>
                  {t("action.delete", "Obriši")}
                </button>
              </span>
            )}
          </div>
        )}
      </section>

      <div className="asset-grid">
        <div className="asset-col">
        {/* Type */}
        <section className="card">
          <div className="card-h"><h3>{t("assets.section.type", "Tip")}</h3></div>
          <div className="card-b">
            <dl className="kv">
              <dt>{t("assets.field.type", "Tip")}</dt>
              <dd><PermLink permission="types:view" to={`/tipovi/${asset.typeId}`}><span className="mono" style={{ fontSize: 12.5 }}>{asset.typeCode}</span> {asset.typeName}</PermLink></dd>
            </dl>
            {canViewTypes && (
              <Link to={`/pretraga?typeId=${asset.typeId}`} className="btn sm" style={{ marginTop: 12, textDecoration: "none" }}>
                <Ic.search /> {t("assets.findAlternatives", "Alternative istog tipa")}
              </Link>
            )}
          </div>
        </section>

      {/* Actual values + piece data (one PUT) */}
      <section className="card">
        <div className="card-h">
          <div>
            <h3>{t("assets.section.values", "Stvarne vrednosti")}</h3>
            <div className="sub">{t("assets.valuesSub", "Izmerene / stvarne vrednosti ovog komada, prema atributima tipa.")}</div>
          </div>
          {canEdit && !editingPiece && (
            <button type="button" className="btn sm" onClick={() => setEditingPiece(true)}>
              <Ic.pencil /> {t("action.edit", "Izmeni")}
            </button>
          )}
        </div>
        {!editingPiece ? (
          <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {asset.attributeDefs.length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>{t("assets.valuesEmpty", "Tip nema definisane atribute.")}</div>
            ) : (
              <AttributeValuesView defs={asset.attributeDefs} values={asset.actualValues} />
            )}
            <div className="section-h" style={{ marginTop: 4 }}><h3>{t("assets.section.piece", "Podaci komada")}</h3></div>
            <dl className="kv-grid">
              {asset.isCustomMade ? (
                <div className="kv-grid-item"><dt>{t("assets.field.origin", "Poreklo")}</dt><dd>{t("assets.field.isCustomMade", "Sopstvena izrada (radionica)")}</dd></div>
              ) : (
                <>
                  <div className="kv-grid-item"><dt>{t("assets.field.manufacturer", "Proizvođač")}</dt><dd className={asset.manufacturer ? undefined : "muted"}>{asset.manufacturer || "—"}</dd></div>
                  <div className="kv-grid-item"><dt>{t("assets.field.manufacturerCode", "Oznaka proizvođača")}</dt><dd className={asset.manufacturerCode ? undefined : "muted"}>{asset.manufacturerCode || "—"}</dd></div>
                  <div className="kv-grid-item">
                    <dt>{t("assets.field.supplier", "Dobavljač")}</dt>
                    <dd className={asset.supplier ? undefined : "muted"}>
                      {asset.supplier && asset.supplierId && canOpenSupplier
                        ? <Link to={`/admin/dobavljaci/${asset.supplierId}`}>{asset.supplier}</Link>
                        : asset.supplier || "—"}
                    </dd>
                  </div>
                </>
              )}
              <div className="kv-grid-item"><dt>{t("assets.field.serialNo", "Serijski broj")}</dt><dd className={asset.serialNo ? "mono" : "muted"}>{asset.serialNo || "—"}</dd></div>
            </dl>
            {asset.note && (
              <div className="kv-grid-item"><dt style={kvLabel}>{t("assets.field.note", "Napomena")}</dt><dd style={{ margin: "2px 0 0", whiteSpace: "pre-wrap", fontSize: 13.5 }}>{asset.note}</dd></div>
            )}
          </div>
        ) : (
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {asset.attributeDefs.length === 0 ? (
            <div className="muted" style={{ fontSize: 13 }}>{t("assets.valuesEmpty", "Tip nema definisane atribute.")}</div>
          ) : (
            <AttributeValuesForm defs={asset.attributeDefs} values={values} disabled={!canEdit} onChange={setValues} />
          )}
          <div className="section-h" style={{ marginTop: 4 }}><h3>{t("assets.section.piece", "Podaci komada")}</h3></div>
          <AssetOriginFields
            value={{ manufacturerId, manufacturerCode, supplierId, isCustomMade }}
            onChange={(o) => { setManufacturerId(o.manufacturerId); setManufacturerCode(o.manufacturerCode); setSupplierId(o.supplierId); setIsCustomMade(o.isCustomMade); }}
            disabled={!canEdit}
          />
          <div className="form-grid">
            <label className="field">
              <span>{t("assets.field.serialNo", "Serijski broj")}</span>
              <input className="input" value={serialNo} onChange={(e) => setSerialNo(e.target.value)} disabled={!canEdit} maxLength={100} style={{ fontFamily: "var(--mono)" }} />
            </label>
          </div>
          <label className="field">
            <span>{t("assets.field.note", "Napomena")}</span>
            <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)} disabled={!canEdit} style={textarea} />
          </label>
          <div className="form-actions">
            <button type="button" className="btn" disabled={saving} onClick={() => { apply(asset); setEditingPiece(false); }}>
              {t("action.cancel", "Otkaži")}
            </button>
            <button type="button" className="btn primary" disabled={saving} onClick={savePiece}>
              {saving ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
            </button>
          </div>
        </div>
        )}
      </section>
        </div>

        {/* Where */}
        <section className="card">
          <div className="card-h"><h3>{t("assets.section.where", "Gde je")}</h3></div>
          <div className="card-b">
            <dl className="kv">
              <dt>{t("assets.field.logical", "Logička")}</dt>
              <dd>
                {mounted && asset.positionId ? (
                  <nav className="crumbs" style={{ margin: 0, fontSize: 13 }} aria-label={t("assets.field.position", "Pozicija")}>
                    {positionCrumbs.map((p) => (
                      <span key={p.id}><PermLink permission="structure:view" to={`/struktura/${p.id}`}>{p.name}</PermLink></span>
                    ))}
                    <span><PermLink permission="structure:view" to={`/struktura/${asset.positionId}`}><b>{asset.positionName}</b></PermLink></span>
                  </nav>
                ) : <span className="muted">{t("assets.notMounted", "Nije montiran")}</span>}
              </dd>
              <dt>{t("assets.field.locationEffective", "Fizička lokacija")}</dt>
              <dd>
                {asset.effectiveLocation
                  ? <>{asset.effectiveLocation.name} <span className="muted mono" style={{ fontSize: 12 }}>{asset.effectiveLocation.code}</span>
                    {mounted && <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>{t("assets.locationInherited", "(nasleđena od pozicije)")}</span>}</>
                  : <span className="muted">—</span>}
              </dd>
              {!mounted && (
                <>
                  <dt>{t("assets.field.locationNote", "Napomena lokacije")}</dt>
                  <dd>{asset.locationNote || <span className="muted">—</span>}</dd>
                </>
              )}
              {asset.expectedReturnAt && (
                <>
                  <dt>{t("assets.field.expectedReturnAt", "Očekivani povratak sa servisa")}</dt>
                  <dd>{formatDate(asset.expectedReturnAt)}</dd>
                </>
              )}
              {mounted && asset.replacementKind && (
                <>
                  <dt>{t("mount.kind", "Vrsta zamene")}</dt>
                  <dd>
                    {t(`assets.replacementKind.${asset.replacementKind}`, asset.replacementKind === "TEMPORARY" ? "Privremena" : "Trajna")}
                    {asset.replacementReminderAt && <span className="muted"> · {t("assets.reminderAt", "podsetnik {{date}}", { date: formatDate(asset.replacementReminderAt) })}</span>}
                  </dd>
                </>
              )}
            </dl>
            {asset.effectiveLocation?.lat != null && asset.effectiveLocation?.lng != null && (
              <div style={{ marginTop: 12 }}>
                <MapPicker key={asset.effectiveLocation.id} lat={asset.effectiveLocation.lat} lng={asset.effectiveLocation.lng} readOnly height={200} />
              </div>
            )}
            {mounted && asset.positionId && placements.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="marker-card-label" style={{ marginBottom: 6 }}>{t("layout.whereOnLayout", "Gde je na layoutu")}</div>
                <PlacementPreview placements={placements} positionId={asset.positionId} />
              </div>
            )}
          </div>
        </section>
      </div>

      {/* History */}
      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-h">
          <div>
            <h3>{t("assets.section.history", "Istorija")}</h3>
            <div className="sub">{t("assets.historySub", "Kreiran {{created}} · izmenjen {{updated}}", { created: formatDateTime(asset.createdAt), updated: formatDateTime(asset.updatedAt) })}</div>
          </div>
          <Link to={`/asseti/${asset.id}/putanja`} className="btn sm" style={{ textDecoration: "none" }}>
            <Ic.route /> {t("journey.open", "Putanja asseta")}
          </Link>
        </div>
        <AssetEventsTable load={loadEvents} refreshKey={eventsKey} />
      </section>

      {/* Attachments + notes (sprint 4) */}
      <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
        <AttachmentsPanel entityKind="ASSET" entityId={asset.id} entityCode={asset.tag} title={t("assets.section.attachments", "Slike i dokumenti")} />
        <CommentsPanel entityKind="ASSET" entityId={asset.id} />
      </div>

      {modal === "mount" && (
        <MountModal asset={asset} onClose={() => setModal(null)} onMounted={(d) => afterEvent(d, t("assets.toast.mounted", "Asset montiran."))} />
      )}
      {modal === "remount" && (
        <MountModal asset={asset} mode="remount" onClose={() => setModal(null)} onMounted={(d) => afterEvent(d, t("assets.toast.remounted", "Asset premešten na novu poziciju."))} />
      )}
      {modal === "dismount" && (
        <DismountModal asset={asset} onClose={() => setModal(null)} onDone={(d) => afterEvent(d, t("assets.toast.dismounted", "Asset demontiran."))} />
      )}
      {modal === "move" && (
        <MoveAssetModal asset={asset} onClose={() => setModal(null)} onDone={(d) => afterEvent(d, t("assets.toast.moved", "Asset premešten."))} />
      )}
      {modal === "condition" && (
        <ConditionModal asset={asset} onClose={() => setModal(null)} onDone={(d) => afterEvent(d, t("assets.toast.condition", "Stanje promenjeno."))} />
      )}
      {modal === "note" && (
        <NoteEventModal asset={asset} onClose={() => setModal(null)} onDone={(d) => afterEvent(d, t("assets.toast.note", "Zapis dodat."))} />
      )}
      {modal === "edit" && (
        <AssetFormModal mode={{ kind: "edit", asset }} onClose={() => setModal(null)} onSaved={(d) => { setModal(null); apply(d); toast.success(t("assets.toast.saved", "Asset sačuvan.")); }} />
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
const kvLabel: CSSProperties = { fontSize: 11, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--ink-3)" };
